import { FORM_SERVICES } from "@/lib/analytics/config";

export interface KitSubscribeResponse {
  subscriber: {
    id: number;
    first_name: string | null;
    email_address: string;
    state: string;
    created_at: string;
    added_at?: string;
    fields: Record<string, unknown>;
    referrer?: string | null;
    referrer_utm_parameters?: Record<string, string>;
  };
}

/**
 * Create a subscriber in Kit (ConvertKit) and add them to a form using the v4 API.
 * First creates/updates the subscriber, then adds them to the specified form.
 * @param formId The form ID to add the subscriber to.
 * @param email The email address to subscribe.
 * @param firstName Optional first name of the subscriber.
 * @param source Optional source identifier for tracking purposes (stored in custom fields).
 * @param referrer Optional referrer URL for tracking.
 * @returns Promise resolving to the subscriber data or throwing an error.
 */
export async function subscribeToKit(
  formId: string,
  email: string,
  firstName?: string,
  source?: string,
  referrer?: string
): Promise<KitSubscribeResponse> {
  const apiKey = FORM_SERVICES.CONVERTKIT_API_KEY;

  // Step 1: Create or update the subscriber
  const createSubscriberUrl = "https://api.kit.com/v4/subscribers";

  const createBody: {
    email_address: string;
    first_name?: string;
    state?: string;
    fields?: Record<string, string>;
  } = {
    email_address: email,
    state: "active",
  };

  if (firstName) {
    createBody.first_name = firstName;
  }

  if (source) {
    createBody.fields = {
      Source: source,
    };
  }

  const createResponse = await fetch(createSubscriberUrl, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "X-Kit-Api-Key": apiKey,
    },
    body: JSON.stringify(createBody),
  });

  const createData = await createResponse.json();

  if (!createResponse.ok) {
    console.error("Kit API - Create subscriber failed:", {
      status: createResponse.status,
      statusText: createResponse.statusText,
      data: createData,
    });
    throw new Error(
      createData.message ||
        createData.error ||
        `Failed to create subscriber: ${createResponse.status}`
    );
  }

  // Step 2: Add subscriber to the form
  // Note: formId should be a numeric form ID (integer), not a ConvertKit v3 form ID string
  const addToFormUrl = `https://api.kit.com/v4/forms/${formId}/subscribers`;

  const addToFormBody: {
    email_address: string;
    referrer?: string | null;
  } = {
    email_address: email,
  };

  if (referrer) {
    addToFormBody.referrer = referrer;
  }

  const addToFormResponse = await fetch(addToFormUrl, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "X-Kit-Api-Key": apiKey,
    },
    body: JSON.stringify(addToFormBody),
  });

  const addToFormData = await addToFormResponse.json();

  if (!addToFormResponse.ok) {
    // If subscriber already exists in form, that's okay (returns 200)
    if (addToFormResponse.status !== 200) {
      console.error("Kit API - Add to form failed:", {
        status: addToFormResponse.status,
        statusText: addToFormResponse.statusText,
        formId,
        data: addToFormData,
      });

      // Provide helpful error message for 404 (form not found)
      if (addToFormResponse.status === 404) {
        throw new Error(
          `Form not found. The form ID "${formId}" may be incorrect. Kit API v4 requires a numeric form ID (integer), not a ConvertKit v3 form ID string. Please check your form ID in the Kit dashboard.`
        );
      }

      throw new Error(
        addToFormData.message ||
          addToFormData.error ||
          `Failed to add subscriber to form: ${addToFormResponse.status}`
      );
    }
  }

  // Return the form subscription response (includes added_at and referrer)
  return addToFormData;
}
